import React from 'react';
import clsx from 'clsx';

/**
 * Skeleton Component
 * Loading placeholder with shimmer animation
 *
 * @param {string} variant - Shape variant: 'text', 'circular', 'rectangular'
 * @param {string} width - Width of skeleton
 * @param {string} height - Height of skeleton
 * @param {string} className - Additional CSS classes
 */
const Skeleton = ({
  variant = 'text',
  width,
  height,
  className,
  count = 1,
  ...props
}) => {
  const baseStyles = 'animate-pulse bg-gradient-to-r from-neutral-200 via-neutral-300 to-neutral-200 bg-[length:200%_100%]';

  const variantStyles = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style = {
    width: width || (variant === 'text' ? '100%' : '40px'),
    height: height || (variant === 'text' ? undefined : '40px'),
  };

  const skeletonClasses = clsx(
    baseStyles,
    variantStyles[variant],
    className
  );

  if (count > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={skeletonClasses}
            style={style}
            {...props}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={skeletonClasses}
      style={style}
      {...props}
    />
  );
};

/**
 * Skeleton Card Component
 * Pre-built skeleton for card layouts
 */
export const SkeletonCard = () => (
  <div className="bg-white rounded-lg border border-neutral-200 p-6 space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton variant="circular" width="48px" height="48px" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton variant="text" count={3} />
    </div>
    <Skeleton variant="rectangular" height="40px" />
  </div>
);

/**
 * Skeleton Table Row Component
 */
export const SkeletonTableRow = () => (
  <tr className="border-b border-neutral-200">
    <td className="px-4 py-3">
      <Skeleton variant="text" width="80%" />
    </td>
    <td className="px-4 py-3">
      <Skeleton variant="text" width="60%" />
    </td>
    <td className="px-4 py-3">
      <Skeleton variant="text" width="50%" />
    </td>
    <td className="px-4 py-3">
      <Skeleton variant="circular" width="32px" height="32px" />
    </td>
  </tr>
);

/**
 * Skeleton List Item Component
 */
export const SkeletonListItem = () => (
  <div className="flex items-center gap-4 p-4 border border-neutral-200 rounded-lg">
    <Skeleton variant="circular" width="40px" height="40px" />
    <div className="flex-1 space-y-2">
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="50%" />
    </div>
    <Skeleton variant="rectangular" width="80px" height="32px" />
  </div>
);

export default Skeleton;
